import { Textarea } from "@/src/components/ui/textarea";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/src/components/ui/button";
import { useForm, FormProvider } from "react-hook-form";
import { useLoaderData, useNavigate } from "react-router-dom";
import { PatchTopLevelRegionValues, PatchTopLevelRegionSchema } from "../schemas";
import { Label } from "@/src/components/ui/label";
import { RelationMultiInput } from "../components/RelationMultiInput";
import { SuccessResponse, fetch } from "@/src/lib/fetch";
import { Country, TopLevelRegion } from "../model";
import { extractFromMultiName } from "../utils";
import { FormErrors } from "../components/FormErrors";

export async function topLevelRegionLoader({ params }: { params: { tlrId: string } }) {
  const response = await fetch<SuccessResponse<TopLevelRegion>>(`/api/locations/top-level-regions/${params.tlrId}`);
  return { defaultValues: response.data, tlrId: params.tlrId };
}

export function EditTopLevelRegion() {
  const { fetch } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { defaultValues, tlrId } = useLoaderData() as {
    defaultValues: Partial<PatchTopLevelRegionValues>;
    tlrId: string;
  };
  const methods = useForm<PatchTopLevelRegionValues>({
    resolver: zodResolver(PatchTopLevelRegionSchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = methods;

  const loadCountries = async (q: string) => {
    const data = await fetch<{ data: Country[] }>(
      `/api/locations/countries?page=1&pageSize=100&filter=${encodeURIComponent(q)}`,
    );
    return data.data;
  };

  const onSubmit = async (data: PatchTopLevelRegionValues) => {
    try {
      await fetch(`/api/locations/top-level-regions/${tlrId}`, "PATCH", data);
      toast({ title: "Success", description: "Top-Level Region updated successfully" });
      navigate(`/locations/top-level-regions/${tlrId}`);
    } catch (error) {
      toast({ title: "Error", description: "Failed to update Top-Level Region", variant: "destructive" });
    }
  };

  const onDelete = async () => {
    const shouldDelete = window.confirm("Are you sure you want to delete this top-level region?");
    if (!shouldDelete) return;

    try {
      await fetch(`/api/locations/top-level-regions/${tlrId}`, "DELETE");
      toast({ title: "Success", description: "Top-Level Region deleted successfully" });
      navigate("/locations/top-level-regions");
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete Top-Level Region", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-2xl shadow-md bg-white p-4">
      <h2 className="text-lg font-semibold">Edit Top-Level Region - {extractFromMultiName(defaultValues.names)}</h2>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
          <div>
            <Label>Names</Label>
            <Textarea {...register("names")} placeholder="Enter names, one per line" />
            {errors.names && <p className="text-red-500">{errors.names.message}</p>}
          </div>

          <RelationMultiInput name="countries" label="Countries" loadOptions={loadCountries} />

          <FormErrors errors={errors} />

          <Button type="submit">Save</Button>
          <Button type="button" onClick={onDelete} variant="destructive" className="ml-2">
            Delete
          </Button>
        </form>
      </FormProvider>
    </div>
  );
}
